import { TableIcon } from "lucide-react";
import { useState } from "react";
import { Button, Popover } from "react-aria-components";
import { tv } from "tailwind-variants";

const MAX_ROWS = 10;
const MAX_COLS = 10;

const buttonClassName = tv({
  base: "rounded-md p-2 cursor-pointer hover:bg-gray-200 disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed",
  variants: {
    active: {
      true: "bg-gray-300",
    },
  },
});

type TableSizePickerProps = {
  onSelect: (rows: number, columns: number) => void;
};

export function TableSizePicker({ onSelect }: TableSizePickerProps) {
  const [hoveredCell, setHoveredCell] = useState<{
    row: number;
    col: number;
  } | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleCellClick = (row: number, col: number) => {
    onSelect(row + 1, col + 1);
    setIsOpen(false);
    setHoveredCell(null);
  };

  const isCellHighlighted = (row: number, col: number) => {
    if (!hoveredCell) return false;
    return row <= hoveredCell.row && col <= hoveredCell.col;
  };

  return (
    <>
      <Button
        onPress={() => setIsOpen(!isOpen)}
        className={buttonClassName()}
        aria-label="Insert Table"
      >
        <TableIcon size={16} />
      </Button>
      {isOpen && (
        <Popover
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          className="absolute z-50 mt-1 rounded-md border border-gray-300 bg-white p-3 shadow-lg"
        >
          <div className="flex flex-col gap-2">
            <div className="text-center text-sm font-medium text-gray-700">
              {hoveredCell
                ? `${hoveredCell.row + 1} × ${hoveredCell.col + 1}`
                : "Select table size"}
            </div>
            <div className="grid gap-0.5">
              {Array.from({ length: MAX_ROWS }, (_, rowIndex) => (
                <div key={rowIndex} className="flex gap-0.5">
                  {Array.from({ length: MAX_COLS }, (_, colIndex) => (
                    <button
                      key={colIndex}
                      type="button"
                      className={`h-5 w-5 border border-gray-300 transition-colors hover:border-gray-400 ${
                        isCellHighlighted(rowIndex, colIndex)
                          ? "bg-blue-200"
                          : "bg-white"
                      }`}
                      onMouseEnter={() =>
                        setHoveredCell({ row: rowIndex, col: colIndex })
                      }
                      onClick={() => handleCellClick(rowIndex, colIndex)}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Popover>
      )}
    </>
  );
}
